import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable, of, OperatorFunction} from 'rxjs';
import {Branch} from '../../_data/admin/branch';
import {first, map, mergeMap, tap} from 'rxjs/operators';
import {AddBranch} from '../../_data/admin/add-branch';
import {MemberInfo} from '../../_data/admin/member-info';
import {EditBranch} from '../../_data/admin/edit-branch';
import {I18nService} from '../gui/i18n.service';
import {ConfirmModalService} from '../gui/confirm-modal.service';
import {uEncodeURI} from '../../_utils/lang';
import {NotificationService} from '../gui/notification.service';
import {mapErrorToHttpResponse, tapStatusToNotification} from '../../_utils/operators';
import {EAML_PRODUCT_CODES, IDX_PREFIX} from '../../_constants/constants';
import {ProductInfo} from '../../_data/admin/products';
import {ApiEndpoints} from '../../_constants/api-endpoints';

@Injectable({
  providedIn: 'root'
})
export class BranchesService {
  private http = inject(HttpClient);
  private i18nService = inject(I18nService);
  private modalService = inject(ConfirmModalService);
  private notificationService = inject(NotificationService);
  private apiEndpoints = inject(ApiEndpoints);


  private modifyBranchErrorHandler(): OperatorFunction<any, HttpResponse<string>> {
    return mapErrorToHttpResponse(this.notificationService, [
      {statusCode: 304, i18nMessage: 'content.branch-not-updated'},
      {statusCode: 409, i18nMessage: 'content.branch-already-exists'},
      {statusCode: 412, i18nMessage: 'content.company-does-not-exist'}
    ]);
  }

  private notifyBranchStatus<T>() {
    return tapStatusToNotification<T>(this.notificationService, [
      {statusCode: 200, i18nMessage: 'content.branch-updated'},
      {statusCode: 201, i18nMessage: 'content.branch-created'}
    ]);
  }

  private addEditBranch(url: string, data: AddBranch | EditBranch): Observable<HttpResponse<string>> {
    return this.http.post(url, data, {observe: 'response', responseType: 'text'}).pipe(
      this.modifyBranchErrorHandler(), this.notifyBranchStatus()
    );
  }

  getBranches(companyName: string): Observable<HttpResponse<Branch[]>> {
    console.debug(`getting branches from service for ${companyName}...`);
    return this.http.post<Branch[]>(this.apiEndpoints.branchesForCompany, {companyName: companyName}, {observe: 'response'});
  }

  getMemberCodeInfo(memberCode: string): Observable<MemberInfo> {
    return this.http.post<MemberInfo>(this.apiEndpoints.memberCodeInfo, {memberCode: memberCode, lang: this.i18nService.currentLangShortCode}).pipe(
      tap(m => {
        if(!m) {
          console.warn(`Member Info not found for ${memberCode}`);
          this.notificationService.displayI18n('content.branch-not-found', {memberCode: memberCode});
        }
      }),
      mergeMap(m => {

        const productCodes = m.si.productInfos.map(pi => pi.product);
        // console.debug(`Checking if ${m.memberCode ?? m.si?.memberCode}, with products ${productCodes}, contains the products for EAML (${EAML_PRODUCT_CODES})`);

        //Check if branch needs Enhanced AML injected
        if(EAML_PRODUCT_CODES.every(p => productCodes.includes(p))) {
          console.debug('Injecting EAML ProductInfo');
          return this.i18nService.getTranslation('content.ifs-enhanced-aml').pipe(first(), map(translation => {
            const eaml: ProductInfo = {allow: 'Y', product: '99980', productName: translation, productReportType: 'R', productType: ''};
            m.si.productInfos.push(eaml);
            console.debug(`Resulting productInfos: ${JSON.stringify(m.si.productInfos)}`);
            return m;
          }));
        } else {
          // console.debug('It does not.  NOT injecting EAML pi');
          return of(m);
        }

      })
    );
  }

  /** Gets the member info for a given membercode and if any of the products start with the IDX prefix, it's an IDX branch */
  isIdxBranch(memberCode: string): Observable<boolean> {
    return this.getMemberCodeInfo(memberCode).pipe(map(memberInfo => !!memberInfo?.si?.productInfos?.find(pi => pi.productName.startsWith(IDX_PREFIX))));
  }

  getBranch(companyName: string, branchName: string): Observable<Branch> {
    console.debug(`Getting info for "${companyName}"/${branchName}`);
    return this.getBranches(companyName).pipe(map((value: HttpResponse<Branch[]>) => {
      if(value.status === 200) {
        return value.body.find((branch: Branch) => branch.branchName === branchName);
      }
    }));
  }

  addBranch(addBranch: AddBranch): Observable<HttpResponse<string>> {
    return this.addEditBranch(this.apiEndpoints.branchesAdd, addBranch);
  }

  editBranch(editBranch: EditBranch): Observable<HttpResponse<string>> {
    return this.addEditBranch(this.apiEndpoints.branchesEdit, editBranch);
  }

  deleteBranch(companyName: string, branchName: string): Observable<boolean> {
    return this.http.delete(
      `${this.apiEndpoints.branchesDelete}?companyName=${uEncodeURI(companyName)}&branchName=${uEncodeURI(branchName)}`, {observe: 'response', responseType: 'text'})
      .pipe(
        mapErrorToHttpResponse(this.notificationService, [], {i18nMessage: 'content.branch-not-deleted'}),
        map(response => response.status === 200),
        tap(success => {
          if(success) {
            this.notificationService.displayI18n('content.branch-deleted');
          } else {
            this.notificationService.displayI18n('content.branch-not-deleted');
          }
        })
      );
  }

  confirmDeleteBranch(companyName: string, branchName: string, ariaLabelledBy: string, container: HTMLElement): Observable<boolean> {
    return this.modalService.open(
      {i18nKey: 'content.delete-something', data: {what: branchName}},
      {i18nKey: 'content.confirm-delete-something', data: {what: branchName}},
      ariaLabelledBy,
      container
    ).result$.pipe(mergeMap(accepted => {
      if(accepted) {
        console.debug(`OK clicked.  Deleting...`);
        return this.deleteBranch(companyName, branchName);
      } else {
        return of(false);
      }
    }));
  }

}
